class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();
      const addonLines = this.dataset.addons;
      const mainCart = this.closest("cart-items");
      if (addonLines) {
        mainCart.updateQuantities(addonLines, 0, this.dataset.index);
      } else {
        mainCart.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "change",
      debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body },
        });
      }, 300),
    );
  }
}

customElements.define("cart-note", CartNote);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById(
      "shopping-cart-line-item-status",
    );
    this.hoverEvent = new Event("mouseover");
    this.itemCountHolder = document.querySelector("[data-js-cart-item-count]");
    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]'),
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0,
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener("change", this.debouncedOnChange.bind(this));

    this.querySelector("#cart").addEventListener("submit", (event) => {
      const container = this.querySelector(".cart-items");
      const actions = this.querySelector(".cart-items__actions");
      const hasTerms = container.classList.contains("js-has-terms");

      if (!hasTerms) {
        return;
      }

      const isTermsAccepted = container.querySelector("#agree").checked;
      actions.classList.remove("is-hovered");

      if (!isTermsAccepted) {
        event.preventDefault();
        actions.classList.add("is-hovered");
      }
    });

    window.addEventListener("load", () => {
      const container = this.querySelector(".cart-items");
      const hasTerms = container.classList.contains("js-has-terms");

      if (!hasTerms) {
        return;
      }

      const isTermsAccepted = container.querySelector("#agree").checked;
      this.handleTermsCheckbox(isTermsAccepted);
    });

    // Sweep orphaned add-on lines on cart page load. Orphans render nothing
    // in cart-item.liquid (no line, no remove button) yet are still charged;
    // the cart page is a common landing point for carts polluted in another
    // tab or by external rebuilds. While lines are being removed the
    // index-based quantity/remove controls are blocked: deleting a line
    // renumbers every line below it, so a click against the stale DOM could
    // hit the wrong product.
    fetch(`${routes.ajax_api}`)
      .then((response) => response.json())
      .then((cart) => {
        const hasOrphans =
          Object.keys(window.CartAddonGuard.findOrphanUpdates(cart)).length >
          0;
        if (!hasOrphans) {
          return;
        }
        document
          .getElementById("main-cart-items")
          .classList.add("cart__items--disabled");
        return window.CartAddonGuard.sweepThenRender(
          cart,
          {
            sections: this.getSectionsToRender().map(
              (section) => section.section,
            ),
            sectionsUrl: window.location.pathname,
          },
          (finalState, didSweep) => {
            if (didSweep) {
              this.renderState(finalState);
            }
          },
        ).finally(() => {
          this.disableLoading();
        });
      })
      .catch((error) => {
        this.disableLoading();
        window.console.error(
          "[CartAddonGuard] Page-load sweep failed:",
          error,
        );
      });
  }

  onChange(event) {
    const isQuantityInput = event.target.classList.contains("quantity__input");
    const isAcceptTermsInput = event.target.id == "agree";

    if (isAcceptTermsInput) {
      this.handleTermsCheckbox(event.target.checked);
    }

    if (isQuantityInput) {
      const addonLines = event.target.dataset.addons;
      if (addonLines) {
        this.updateQuantities(
          addonLines,
          event.target.value,
          event.target.dataset.index,
        );
      } else {
        this.updateQuantity(
          event.target.dataset.index,
          event.target.value,
          document.activeElement.getAttribute("name"),
        );
      }
    }
  }

  renderState(parsedState) {
    this.classList.toggle("is-empty", parsedState.item_count === 0);

    this.itemCountHolder.innerText = parsedState.item_count;
    this.itemCountHolder.classList.toggle(
      "is-active",
      parsedState.item_count > 0,
    );

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) ||
        document.getElementById(section.id);

      elementToReplace.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector,
      );
    });
  }

  updateQuantities(lines, quantity) {
    const linesObj = {};

    lines.split(",").forEach((line) => {
      this.enableLoading(line);
      linesObj[line] = quantity;
    });

    const sections = this.getSectionsToRender().map(
      (section) => section.section,
    );
    const sectionsUrl = window.location.pathname;
    const body = JSON.stringify({
      updates: {
        ...linesObj,
      },
      sections,
      sections_url: sectionsUrl,
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        return window.CartAddonGuard.sweepThenRender(
          parsedState,
          { sections, sectionsUrl },
          (finalState) => {
            this.renderState(finalState);

            if (quantity == 0) {
              this.updateTrappedFocus();
            }

            this.disableLoading();
          },
        );
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden"),
        );
        // document.getElementById("cart-drawer-errors").textContent =
        //     window.cartStrings.error
        this.disableLoading();
      });
  }

  handleTermsCheckbox(isChecked) {
    this.querySelector(".cart-items__actions").classList.remove("is-hovered");
    this.querySelector(".cart-items").classList.toggle(
      "agreed-to-terms",
      isChecked,
    );
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents-body",
      },
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents-foot",
      },
      {
        id: "cart-live-region-text",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-cart-messages",
      },
      {
        id: "shopping-cart-line-item-status",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-cart-messages",
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const sections = this.getSectionsToRender().map(
      (section) => section.section,
    );
    const sectionsUrl = window.location.pathname;
    const body = JSON.stringify({
      line,
      quantity,
      sections,
      sections_url: sectionsUrl,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        return window.CartAddonGuard.sweepThenRender(
          parsedState,
          { sections, sectionsUrl },
          (finalState) => {
            this.renderState(finalState);

            this.updateLiveRegions(line, finalState.item_count);
            const lineItem = document.getElementById(`CartItem-${line}`);
            if (lineItem && lineItem.querySelector(`[name="${name}"]`))
              lineItem.querySelector(`[name="${name}"]`).focus();
            this.disableLoading();
          },
        );
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden"),
        );
        document.getElementById("cart-errors").textContent =
          window.cartStrings.error;
        this.disableLoading();
      });
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      document
        .getElementById(`CartItem-error-${line}`)
        .querySelector(".cart-item__error-text").innerHTML =
        window.cartStrings.quantityError.replace(
          "[quantity]",
          document.getElementById(`CartItem-quantity-${line}`).value,
        );
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus = document.getElementById("cart-live-region-text");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    document
      .getElementById("main-cart-items")
      .classList.add("cart__items--disabled");
    this.querySelectorAll(`#CartItem-${line} .loading-overlay`).forEach(
      (overlay) => overlay.classList.remove("hidden"),
    );
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading() {
    document
      .getElementById("main-cart-items")
      .classList.remove("cart__items--disabled");
  }
}

customElements.define("cart-items", CartItems);
